import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  AfterViewInit,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { InvestmentWizardService } from '../services/investment-wizard-service.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { WarnDialogComponent } from '../../warn-dialog/warn-dialog.component';
import { EnvironmentService } from '../../services/environment.service';
import { UnifiedFlowService } from '../../unified-flow/unified.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HasPermissionDirective } from '../../directives/permissions.directive';
import { ReasonTextComponent } from '../../shared-components/reason-text/reason-text.component';
import { NgxLoadingModule } from 'ngx-loading';

@Component({
  selector: 'app-program-selection',
  templateUrl: './program-selection.component.html',
  styleUrls: ['./program-selection.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    HasPermissionDirective,
    ReasonTextComponent,
    NgxLoadingModule,
  ],
})
export class ProgramSelectionComponent implements OnInit, AfterViewInit {
  dialog = inject(MatDialog);
  private rbs = inject(RightBridgeApiService);
  private params = inject(ActivatedRoute);
  private ss = inject(SessionStorageService);
  private iws = inject(InvestmentWizardService);
  private envSvc = inject(EnvironmentService);
  private unfFlowSvc = inject(UnifiedFlowService);

  @Output() navigate = new EventEmitter();
  @Output() progSelected = new EventEmitter();

  private environment;
  id: string;
  chartData;
  tableData = [];
  columns = [];
  rows = [];
  catData = {};
  programs = [];
  modelData = { name: '' };
  clientName;
  selProgram: string;
  scoring = { neutral: [], negative: [], positive: [] };
  path: string;
  assmntTypes: any[] = [];
  loading = false;
  breadcrumb = 'program';
  selectionType;
  validationCase = false;
  internalLoading = false;
  globals;
  readOnly = false;
  expanded = false;
  expandedItems: string[] = [];

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.path = this.environment.assets ? this.environment.assets : '';

    this.params.params.subscribe(params => {
      this.id = params.id;
    });

    this.globals = this.ss.get('globals') || {};
    const rights = this.ss.get('rights') || [];
    this.readOnly =
      rights.includes('ReadOnly') || rights.includes('ReadOnlyAccess');

    this.scoring = this.globals.scoring ? this.globals.scoring : this.scoring;

    if (this.scoring['neutral'][0] !== this.scoring['negative'][1]) {
      this.scoring['negative'][1] = this.scoring['neutral'][0];
    }
    if (this.scoring['neutral'][1] !== this.scoring['positive'][0]) {
      this.scoring['positive'][0] = this.scoring['neutral'][1];
    }

    this.unfFlowSvc.setNoteValidity({ app: 'iw', valid: true });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initData();
    }, 250);
  }

  initData() {
    const save = this.unfFlowSvc.getSaving();
    this.unfFlowSvc.setLoading(true);
    if (save) {
      setTimeout(() => {
        this.initData();
      }, 250);
    } else {
      this.setLoading(true);
      this.getCats(this.id);
    }
  }

  expand(event, item) {
    const el = this.closestByClass(event.target, 'fund');
    el.classList.toggle('expanded');
    if ([...el.classList].includes('expanded')) {
      this.expandedItems.push(item.id);
    } else {
      this.expandedItems = this.expandedItems.filter(
        filterItem => filterItem !== item.id
      );
    }
  }

  closestByClass(el, clazz) {
    while (!el.classList.contains(clazz)) {
      el = el.parentNode;
      if (!el) {
        return null;
      }
    }
    return el;
  }

  selectFund(name, program) {
    if (program.id !== this.selProgram && this.selProgram !== 'none') {
      const dialogRef = this.dialog.open(WarnDialogComponent, {
        panelClass: 'warn-dialog',
        data: {
          headline: 'Warning',
          content:
            'If you choose to switch fund programs all your previous fund selections will be cleared.',
          confirm: 'OK',
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'continue') {
          this.progSelected.emit({
            program: program.id,
            selectionType: program.selectionType,
          });
          this.clearSelectedFunds(program);
        }
      });
    } else {
      this.rbs
        .saveProfile(this.id, {
          'InvestmentWizard.SelectedProgram': program.id,
        })
        .subscribe(() => {
          this.progSelected.emit({
            program: program.id,
            selectionType: program.selectionType,
          });
          this.pageNavigate(program);
        });
    }
  }

  clearSelectedFunds(program) {
    this.internalLoading = true;
    this.rbs.selectFunds(this.id, null, true).subscribe(() => {
      this.ss.remove('selectedFunds');
      this.rbs
        .saveProfile(this.id, {
          'InvestmentWizard.SelectedProgram': program.id,
          'InvestmentWizard.IsFinalized': false,
          'InvestmentWizard.FilteredFundFamilies': null,
        })
        .subscribe(() => {
          this.internalLoading = false;
          this.pageNavigate(program);
        });
    });
  }

  getCats(profile) {
    this.setLoading(true);
    this.iws.getPrograms(profile).subscribe(data => {
      const iwVars = data.InvestmentWizard;
      this.selProgram = iwVars?.SelectedProgram || 'none';
      this.programs = data.bridges.map(d => {
        const bullets = d?.outline?.sections?.DescriptionBullets
          ? d.outline.sections.DescriptionBullets.split('~')
          : [];
        const desc = d?.outline?.sections?.ShortDescription || '';
        if (!this.assmntTypes.includes(d.assessmenttype)) {
          this.assmntTypes.push(d.assessmenttype);
        }

        if (d.assessmentType === 'validate') {
          this.expandedItems.push(d.ProgramID);
        }

        return {
          id: d.ProgramID,
          name: d?.outline?.sections?.ShortName || d.name,
          score: d.score / 10,
          selected: d.ProgramID === this.selProgram,
          reasons: d.trueRules,
          description: desc || null,
          bulletList: bullets || null,
          selectionType: d.FundSelectionType,
          assessmentType: d.assessmenttype,
        };
      });
      this.assmntTypes.sort().reverse();
      this.breadcrumb = this.assmntTypes.includes('validate')
        ? 'program-validate'
        : this.breadcrumb;
      this.validationCase = this.assmntTypes.includes('validate');

      this.clientName = `${data.ClientPerson.FirstName} ${data.ClientPerson.LastName}`;

      if (iwVars.SelectedProgram) {
        this.progSelected.emit({
          program: iwVars.SelectedProgram,
          selectionType: data.SelectedProgramData.FundSelectionType,
        });
      }

      this.setLoading(false);
    });
  }

  pageNavigate(program) {
    let step;

    if (program.selectionType.toLowerCase() === 'advisory') {
      step = 'validate';
    } else if (program.assessmentType === 'validate') {
      step = 'fund-allocations';
    } else {
      step = 'investment-model';
    }

    this.navigate.emit(step);
  }

  setLoading(state) {
    this.unfFlowSvc.setLoading(state);
  }
}
